import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, LogOut, Home, PhoneCall,
  ChevronRight, Bell, ExternalLink, Settings,
} from "lucide-react";

const STAFF_ROLES = new Set(["owner", "admin", "sales"]);

/**
 * StaffShell — dark professional layout used by the entire /staff/* portal.
 * Deliberately visually different from the customer app (which uses the
 * Royal Ivory theme) so the sales team feels they're in a distinct tool.
 * Also guards the route: kicks non-staff users out to /staff/login.
 */
export default function StaffShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    // Wait for auth to resolve. `user === null` means "still checking",
    // `user === false` means "not logged in".
    if (user === null) return;
    if (!user || !STAFF_ROLES.has(user.role)) {
      nav("/staff/login", { replace: true });
      return;
    }
    api.get("/staff/me")
      .then(({ data }) => setMe(data))
      .catch(() => nav("/staff/login", { replace: true }));
  }, [user, nav]);

  if (!me) {
    return (
      <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center text-gray-400">
        <div className="animate-pulse text-sm tracking-widest uppercase">Loading portal…</div>
      </div>
    );
  }

  const isOwner = me.role === "owner" || me.role === "admin";

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
      isActive
        ? "bg-[#7A1E2A] text-white"
        : "text-gray-300 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-gray-100" data-testid="staff-shell">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-[#0F1420] border-r border-white/5 flex flex-col">
        <div className="px-5 py-5 border-b border-white/5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C9A24A] to-[#7A1E2A] flex items-center justify-center text-white font-black">T</div>
            <div>
              <p className="font-semibold text-sm text-white">To-Let Rentals</p>
              <p className="text-[10px] uppercase tracking-widest text-[#C9A24A] font-bold">Staff Portal</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          <NavLink to="/staff" end className={linkCls} data-testid="staff-nav-dashboard">
            <LayoutDashboard size={16} /> Leads
            {me.counts?.new_leads > 0 && (
              <span className="ml-auto text-[10px] font-bold bg-[#FF5A3D] text-white px-1.5 py-0.5 rounded-full">
                {me.counts.new_leads}
              </span>
            )}
          </NavLink>
          {isOwner && (
            <NavLink to="/staff/team" className={linkCls} data-testid="staff-nav-team">
              <Users size={16} /> Team members
            </NavLink>
          )}
          {isOwner && (
            <a
              href="/admin"
              data-testid="staff-nav-admin-console"
              className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
            >
              <Settings size={16} /> Admin console
              <ExternalLink size={11} className="ml-auto text-gray-500" />
            </a>
          )}
        </nav>

        {/* User block */}
        <div className="p-3 border-t border-white/5">
          <div className="px-2 py-2 mb-2">
            <p className="text-sm font-semibold text-white truncate">{me.name}</p>
            <p className="text-[11px] text-gray-500 truncate">{me.email}</p>
            <p className="text-[10px] uppercase tracking-widest text-[#C9A24A] font-bold mt-1">{me.role}</p>
          </div>
          <button
            onClick={() => nav("/")}
            data-testid="staff-switch-to-customer"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
          >
            <ExternalLink size={13} /> View customer site
          </button>
          <button
            onClick={async () => {
              await logout();
              nav("/staff/login");
            }}
            data-testid="staff-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64 min-h-screen">
        <div className="max-w-7xl mx-auto p-6 lg:p-10">
          <Outlet context={{ me, isOwner, refresh: () => api.get("/staff/me").then(({ data }) => setMe(data)) }} />
        </div>
      </main>
    </div>
  );
}
